import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { AdmissionApplication } from '../../../types/admissions';

interface Props {
  open: boolean;
  application: AdmissionApplication;
  onCancel: () => void;
  onDone: () => Promise<void> | void;
}

const OfferDeclineDialog: React.FC<Props> = ({ open, application, onCancel, onDone }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => { if (!busy) { setReason(''); onCancel(); } };

  const submit = async () => {
    setBusy(true);
    try {
      await admissionApplicationService.recordOfferDecline(application._id, reason.trim() || undefined);
      enqueueSnackbar('Offer decline recorded', { variant: 'success' });
      await onDone();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to record decline', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" fontWeight={700}>Record offer decline</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Recording that <b>{application.name}</b>'s parent declined the offer. This moves the
            application to <b>Offer declined</b> (terminal) and frees up the seat for the waitlist.
          </Typography>
          <TextField
            label="Reason (optional)"
            placeholder="E.g. chose another school; distance"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            helperText="Feeds the lost-reason analysis report."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          disableElevation
          onClick={submit}
          disabled={busy}
        >
          {busy ? 'Recording…' : 'Record decline'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfferDeclineDialog;
